import { Component, OnInit } from '@angular/core';
import { MessageService } from 'primeng/api';
import { ItOpsControllerService } from 'src/app/admin-api/api/itOpsController.service';

@Component({
  selector: 'app-integrity-history',
  templateUrl: './integrity-history.component.html',
  styleUrls: ['./integrity-history.component.scss']
})
export class IntegrityHistoryComponent implements OnInit {
  data: any;
  options: any;
  years: any[] = [];
  selectedYear: number = new Date().getFullYear();
  loading: boolean = false;
  hasData: boolean = true;

  months: any[] = [];
  selectedMonth: number | undefined = new Date().getMonth() + 1;

  constructor(
    private itOpsService: ItOpsControllerService,
    private messageService: MessageService
  ) {}

  ngOnInit() {
    this.initYears();
    this.initMonths();
    this.initChartOptions();
    this.loadData();
  }

  initYears() {
    const currentYear = new Date().getFullYear();
    for (let i = 0; i < 5; i++) {
      this.years.push({ label: (currentYear - i).toString(), value: currentYear - i });
    }
  }

  initMonths() {
    this.months = [
      { label: 'Todos', value: undefined },
      { label: 'Janeiro', value: 1 },
      { label: 'Fevereiro', value: 2 },
      { label: 'Março', value: 3 },
      { label: 'Abril', value: 4 },
      { label: 'Maio', value: 5 },
      { label: 'Junho', value: 6 },
      { label: 'Julho', value: 7 },
      { label: 'Agosto', value: 8 },
      { label: 'Setembro', value: 9 },
      { label: 'Outubro', value: 10 },
      { label: 'Novembro', value: 11 },
      { label: 'Dezembro', value: 12 }
    ];
  }

  initChartOptions() {
    const documentStyle = getComputedStyle(document.documentElement);
    const textColor = documentStyle.getPropertyValue('--text-color');
    const textColorSecondary = documentStyle.getPropertyValue('--text-color-secondary');
    const surfaceBorder = documentStyle.getPropertyValue('--surface-border');

    this.options = {
      maintainAspectRatio: false,
      aspectRatio: 0.8,
      plugins: {
        legend: {
          labels: {
            color: textColor
          }
        },
        tooltip: {
            mode: 'index',
            intersect: false,
            filter: (tooltipItem: any) => {
              return tooltipItem.raw > 0;
            }
        }
      },
      scales: {
        x: {
          ticks: {
            color: textColorSecondary,
            font: {
              weight: 500
            }
          },
          grid: {
            color: surfaceBorder,
            drawBorder: false
          }
        },
        y: {
          ticks: {
            color: textColorSecondary
          },
          grid: {
            color: surfaceBorder,
            drawBorder: false
          }
        }
      }
    };
  }

  loadData() {
    this.loading = true;
    this.itOpsService.getIntegrityHistory(this.selectedYear, this.selectedMonth).subscribe({
      next: (response) => {
        const historyList = response.result;
        if (!historyList || historyList.length === 0) {
          this.hasData = false;
          this.data = null;
          this.loading = false;
          return;
        }

        this.hasData = true;
        this.processChartData(historyList);
        this.loading = false;
      },
      error: () => {
        this.messageService.add({ severity: 'error', summary: 'Erro', detail: 'Falha ao carregar histórico.' });
        this.loading = false;
        this.hasData = false;
      }
    });
  }

  processChartData(historyList: any[]) {
    // Determine view mode based on selectedMonth
    const isMonthlyView = !this.selectedMonth;

    // Labels
    let labels: string[] = [];
    let dataLength = 0;

    if (isMonthlyView) {
      labels = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];
      dataLength = 12;
    } else {
      const daysInMonth = new Date(this.selectedYear, this.selectedMonth!, 0).getDate();
      labels = Array.from({length: daysInMonth}, (_, i) => (i + 1).toString());
      dataLength = daysInMonth;
    }

    // Group by Integrity Name and calculate Max Peak for Top 15 logic
    const map = new Map<string, any>(); 
    const integrityPeaks = new Map<string, number>();

    historyList.forEach(item => {
      if (!map.has(item.nomeIntegridade)) {
        map.set(item.nomeIntegridade, {
          label: item.nomeIntegridade,
          borderColor: item.integrityColor || '#36A2EB',
          // fill: false,
          tension: 0.4,
          data: Array(dataLength).fill(0)
        });
        integrityPeaks.set(item.nomeIntegridade, 0);
      }
      
      const entry = map.get(item.nomeIntegridade);
      let value = 0;
      
      if (isMonthlyView) {
        if (item.month >= 1 && item.month <= 12) {
          value = item.peakErrors;
          entry.data[item.month - 1] = value;
        }
      } else {
        // Daily View
        if (item.day >= 1 && item.day <= dataLength) {
           value = item.peakErrors;
           entry.data[item.day - 1] = value;
        }
      }

      // Track max peak for this integrity
      if (value > (integrityPeaks.get(item.nomeIntegridade) || 0)) {
        integrityPeaks.set(item.nomeIntegridade, value);
      }
    });

    // Identify Top 15 Integrities
    const sortedIntegrities = Array.from(integrityPeaks.entries())
      .sort((a, b) => b[1] - a[1]) // Sort by Peak Descending
      .map(entry => entry[0]);
    
    const top15 = new Set(sortedIntegrities.slice(0, 15));

    const datasets = Array.from(map.values()).map(dataset => {
      // Hide if not in Top 15
      if (!top15.has(dataset.label)) {
        dataset.hidden = true;
      }
      return dataset;
    });

    this.data = {
      labels: labels,
      datasets: datasets
    };

    this.processTableData(historyList, isMonthlyView);
  }

  tableData: any[] = [];

  processTableData(historyList: any[], isMonthlyView: boolean) {
    const map = new Map<string, any>();

    // First pass: Group all items
    historyList.forEach(item => {
      if (!map.has(item.nomeIntegridade)) {
        map.set(item.nomeIntegridade, {
          id: item.integrityId,
          name: item.nomeIntegridade,
          color: item.integrityColor,
          items: []
        });
      }
      map.get(item.nomeIntegridade).items.push(item);
    });

    const result: any[] = [];

    map.forEach(value => {
      const items = value.items;
      
      // Calculate Peak (Max PeakErrors)
      const maxPeak = Math.max(...items.map((i: any) => i.peakErrors || 0));

      // Calculate Average Stability
      const totalStability = items.reduce((sum: number, i: any) => sum + (i.stabilityScore || 0), 0);
      const avgStability = items.length > 0 ? totalStability / items.length : 0;

      // Calculate Trend
      // Sort items by time to determine last and prev
      if (isMonthlyView) {
        items.sort((a: any, b: any) => a.month - b.month);
      } else {
        items.sort((a: any, b: any) => a.day - b.day);
      }

      let trend = 'stable'; // 'up', 'down', 'stable'
      if (items.length >= 2) {
        const last = items[items.length - 1].peakErrors || 0;
        const prev = items[items.length - 2].peakErrors || 0;
        if (last > prev) trend = 'up';
        else if (last < prev) trend = 'down';
      }

      result.push({
        name: value.name,
        color: value.color,
        peak: maxPeak,
        stability: avgStability,
        trend: trend
      });
    });

    // Sort by Peak Descending
    result.sort((a, b) => b.peak - a.peak);

    this.tableData = result;
  }

  onYearChange() {
    this.loadData();
  }

  onMonthChange() {
    this.loadData();
  }
}
